import React, { useState, useContext } from "react";
import { Table, TableRow, TableBody, TableHead } from "@material-ui/core";

import StyledTableCell, {
  HeadRow,
} from "../../common/styled-table-cell.component";
import { GET_STOCKPILES_INPUT } from "../../../queries/stockpiles.gql";
import {
  STOCKPILES_TABLE_COLUMNS_NUMBER,
  MATCHING_ERRORS_MESSAGES,
  APP_INSIGHTS_EVENTS,
  STOCKPILES_TABLE_FEATURES,
} from "../../../constants";
import { MatchContext } from "../../../context";
import {
  SET_FORECASTED_STOCKPILE,
  SET_HIGHLIGHTED_ROWS,
} from "../../../reduce/actionTypes";
import TableCellErrorMessage from "../../common/table-cell-error-message.component";
import DataCell from "../../common/data-cell.component";
import StockpileHeader from "../../common/stockpile-header.component";
import Input from "./input.component";
import IntraTableMessage from "../../HOC/intratable-message/intratable-message.component";
import LoaderBar from "../../common/loader-bar.component";
import ErrorMessage from "../../common/error-message.component";
import useStyles from "../../tables-container/styles";
import useHighlightTableItem from "../../../utils/hooks/use-higlight-table-item";
import usePollStockpiles from "../../../utils/hooks/use-poll-stockpiles";
import prepareRowStyles from "../../../utils/prepare-row-styles";

const FilledTable = ({
  handleChangeTableWeights,
  handleChangeTableInputs,
  stockpilesTableFeatures,
}): JSX.Element => {
  const classes = useStyles();
  const [hoveredStockpile, setHoveredStockpile] = useState("");
  const [hoveredFeature, setHoveredFeature] = useState("");
  const { state, dispatch } = useContext(MatchContext);
  const higlightItem = useHighlightTableItem();
  const { rows, error } = usePollStockpiles(
    GET_STOCKPILES_INPUT,
    stockpilesTableFeatures,
    "getInputData"
  );

  const handleStockpileDoubleClick = (item: string) => {
    const value: string = item;
    if (state.stockpileName) {
      dispatch({
        type: SET_FORECASTED_STOCKPILE,
        payload: value,
      });
    }
  };

  return (
    <React.Fragment>
      {error || state.userInputError ? (
        <ErrorMessage error={error} />
      ) : state.loadingStockpilesData ? (
        <IntraTableMessage>
          <LoaderBar />
        </IntraTableMessage>
      ) : rows[0] && !rows[0].stockpiles ? (
        <ErrorMessage
          error={error}
          customMessage={MATCHING_ERRORS_MESSAGES.MATCHING_ALGORYTHM}
        />
      ) : (
        <Table aria-label="customized table">
          <TableHead>
            <HeadRow>
              <StyledTableCell className={classes.weightCell} align="center">
                Weight
              </StyledTableCell>
              <StyledTableCell className={classes.featureCell} align="center">
                Feature
              </StyledTableCell>
              <StyledTableCell className={classes.headInputCell} align="center">
                Input
              </StyledTableCell>
              <StockpileHeader
                data={rows}
                handleStockpileDoubleClick={handleStockpileDoubleClick}
                handleStockpileHover={(item: string) =>
                  setHoveredStockpile(item)
                }
                hoveredStockpile={hoveredStockpile}
                columnsNumber={STOCKPILES_TABLE_COLUMNS_NUMBER}
              />
            </HeadRow>
          </TableHead>
          <TableBody className={state.hideMatchingTable && "hidden"}>
            {rows.map((row: any) => {
              const feature: string = row.feature.toUpperCase();
              return (
                <TableRow
                  key={feature}
                  className={prepareRowStyles(row, state, hoveredFeature, true)}
                >
                  <StyledTableCell align="center">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={state.tableWeights[feature]}
                      id={feature}
                      onChange={handleChangeTableWeights}
                      datatype="weight"
                    />
                    <TableCellErrorMessage
                      feature={feature}
                      type="weight"
                      errors={state.inputErrors}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    onClick={() =>
                      higlightItem(
                        feature,
                        SET_HIGHLIGHTED_ROWS,
                        "highlightedRows",
                        APP_INSIGHTS_EVENTS.CHANGE_ROW_HIGHLIGHT
                      )
                    }
                    className={classes.dataCell}
                    onMouseEnter={() => setHoveredFeature(feature)}
                    onMouseLeave={() => setHoveredFeature("")}
                  >
                    {row.feature === STOCKPILES_TABLE_FEATURES.GRAIN_SIZE_T1
                      ? "% Type 1"
                      : row.feature === STOCKPILES_TABLE_FEATURES.GRAIN_SIZE_T2
                      ? "% Type 2"
                      : row.feature === STOCKPILES_TABLE_FEATURES.GRAIN_SIZE_T6
                      ? "% Type 6"
                      : row.feature ===
                        STOCKPILES_TABLE_FEATURES.GRAIN_SIZE_T1_GRAIN_SIZE_T6
                      ? "% Type 1 + % Type 6"
                      : row.feature.replace(/_/g, " ")}
                  </StyledTableCell>
                  <StyledTableCell className={classes.inputCell} align="center">
                    <Input
                      type="number"
                      placeholder="-"
                      min="0"
                      max="100"
                      value={state.tableInputs[feature]}
                      id={feature}
                      onChange={handleChangeTableInputs}
                      datatype="input"
                      disabled={
                        row.feature === STOCKPILES_TABLE_FEATURES.ZNFE_RATIO ||
                        row.feature === STOCKPILES_TABLE_FEATURES.ZNPB_RATIO ||
                        row.feature === STOCKPILES_TABLE_FEATURES.PBFE_RATIO ||
                        row.feature === STOCKPILES_TABLE_FEATURES.RPB_RATIO
                      }
                    />
                    <TableCellErrorMessage
                      feature={feature}
                      type="input"
                      errors={state.inputErrors}
                    />
                  </StyledTableCell>
                  <DataCell
                    feature={row.feature}
                    stockpiles={row.stockpiles}
                    handleStockpileDoubleClick={handleStockpileDoubleClick}
                    handleStockpileHover={(item: string) =>
                      setHoveredStockpile(item)
                    }
                    hoveredStockpile={hoveredStockpile}
                  />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </React.Fragment>
  );
};

export default FilledTable;
